import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, from, throwError } from "rxjs";
import { TokenService } from "../services/token";
import { switchMap, catchError } from "rxjs/operators";
import { signOut } from "aws-amplify/auth"; // Importación correcta para Amplify v6
import { Router } from "@angular/router";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private tokenService: TokenService, private router: Router) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Si el token está listo, usarlo
    if (this.tokenService.isTokenReady()) {
      return next
        .handle(this.addTokenToRequest(req, this.tokenService.getToken()))
        .pipe(
          catchError((error: HttpErrorResponse) => {
            // Verificar errores de autenticación
            if (error.status === 401 || error.status === 403) {
              return this.handleAuthError(error);
            }

            // Verificar errores específicos de Cognito
            if (
              error.error &&
              (error.error.message === "Invalid token" ||
                error.error.message === "Token expired" ||
                error.error.message === "The incoming token has expired")
            ) {
              return this.handleInvalidSession();
            }

            return throwError(() => error);
          })
        );
    }

    // Si el token no está listo, esperar a que lo esté
    return from(this.tokenService.refreshToken()).pipe(
      switchMap((token) => {
        return next.handle(this.addTokenToRequest(req, token));
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          return this.handleAuthError(error);
        }
        return throwError(() => error);
      })
    );
  }

  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    // IMPORTANTE: Solo añadir los headers necesarios
    if (token) {
      return request.clone({
        headers: request.headers
          .set("Authorization", `Bearer ${token}`)
          .set("Content-Type", "application/json"),
      });
    }
    return request;
  }

  private handleAuthError(_error: HttpErrorResponse): Observable<never> {
    // Intentar refrescar el token una vez más
    return from(this.tokenService.refreshToken()).pipe(
      switchMap(() => {
        // Si llegamos aquí, el token se refrescó exitosamente
        throw new Error("Retry request with fresh token");
      }),
      catchError(() => {
        // Si falla el refresh, manejar la sesión inválida
        return this.handleInvalidSession();
      })
    );
  }

  private handleInvalidSession(): Observable<never> {
    // Cerrar sesión en Cognito
    signOut().then(() => {
      // Mostrar notificación al usuario
      alert("Su sesión ha expirado. Por favor, inicie sesión nuevamente.");

      // Guardar la URL actual para redirigir después del login
      const currentUrl = window.location.pathname;

      // Redirigir a la página de inicio
      this.router.navigate(["/home"], {
        queryParams: {
          returnUrl: currentUrl,
          sessionExpired: "true",
        },
      });
    });

    return throwError(() => new Error("Sesión expirada"));
  }
}
